import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Check, Star, Zap, Phone } from 'lucide-react';

const Pricing = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const plans = [
    {
      name: "Starter Plan",
      description: "Perfect for small teams getting started",
      originalPrice: "₹36,000",
      price: "₹15,000",
      period: "/year",
      subtitle: "₹5,000 installation + ₹10,000 per year",
      users: "Up to 10 users",
      popular: false,
      features: [
        "Complete project tracking",
        "Task assignment & management",
        "Basic reporting dashboard",
        "Mobile app included",
        "Email support",
        "Free setup & installation"
      ]
    },
    {
      name: "Growth Plan",
      description: "Ideal for growing teams and businesses",
      originalPrice: "₹250",
      price: "₹125",
      period: "per user/month",
      subtitle: "11 to 50 users",
      popular: true,
      features: [
        "Everything in Starter Plan",
        "Advanced reporting & analytics",
        "Department-wise insights",
        "Billable vs non-billable tracking",
        "Priority support",
        "Custom integrations",
        "Advanced user management",
        "Export capabilities"
      ]
    },
    {
      name: "Enterprise Plan",
      description: "For large teams with custom requirements",
      price: "Custom",
      period: "pricing",
      subtitle: "For more than 50 users",
      popular: false,
      features: [
        "Everything in Growth Plan",
        "Custom feature development",
        "Dedicated account manager",
        "On-premise deployment",
        "24/7 phone support",
        "Advanced security features",
        "API access",
        "Training & onboarding"
      ]
    }
  ];

  return (
    <section id="pricing" className="py-20 lg:py-32 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <Badge variant="secondary" className="mb-4 bg-accent/10 text-accent border-accent/20">
            <Star className="w-4 h-4 mr-1" />
            Beta Launch Offer
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
            EfforTrak Pricing -
            <span className="text-gradient-primary block">
              Beta Launch Offer
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Special beta launch pricing valid for one year. Don't miss this limited-time opportunity!
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {plans.map((plan, index) => (
            <Card key={index} className={`relative group hover-lift border-0 shadow-card animate-fade-in ${
              plan.popular 
                ? 'bg-gradient-to-b from-primary/5 to-accent/5 border-2 border-primary/20' 
                : 'bg-card/60 backdrop-blur-sm'
            }`} style={{ animationDelay: `${index * 0.1}s` }}>
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-primary text-white px-4 py-1">
                    <Zap className="w-3 h-3 mr-1" />
                    Most Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-8 pt-8">
                <CardTitle className="text-2xl font-heading font-bold text-foreground mb-2">
                  {plan.name}
                </CardTitle>
                <p className="text-muted-foreground mb-4">{plan.description}</p>
                
                <div className="space-y-2">
                  {plan.originalPrice && (
                    <div className="text-lg text-muted-foreground line-through">
                      {plan.originalPrice}
                    </div>
                  )}
                  <div className="flex items-baseline justify-center space-x-1">
                    <span className="text-4xl font-heading font-bold text-foreground">
                      {plan.price}
                    </span>
                    <span className="text-muted-foreground">{plan.period}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">{plan.subtitle}</div>
                  {plan.users && (
                    <div className="text-sm font-medium text-accent">{plan.users}</div>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground text-sm leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Button 
                  onClick={() => scrollToSection('contact')}
                  className={`w-full transition-all duration-300 ${
                    plan.popular 
                      ? 'bg-gradient-primary hover:shadow-glow' 
                      : 'bg-primary hover:bg-primary/90'
                  }`}
                  size="lg"
                >
                  {plan.name === 'Enterprise Plan' ? (
                    <>
                      <Phone className="w-4 h-4 mr-2" />
                      Contact Sales
                    </>
                  ) : (
                    'Start Free Trial'
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="text-center space-y-6 animate-fade-in">
          <div className="inline-flex items-center space-x-2 bg-warning/10 text-warning px-6 py-3 rounded-full border border-warning/20">
            <Star className="h-5 w-5" />
            <span className="font-semibold">Limited Time: This special beta launch offer is valid for one year</span>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-6 text-center max-w-2xl mx-auto">
            <div className="space-y-1">
              <div className="text-sm font-semibold text-accent">✓ Free Setup</div>
              <div className="text-xs text-muted-foreground">No installation fees</div>
            </div>
            <div className="space-y-1">
              <div className="text-sm font-semibold text-accent">✓ No Lock-in</div>
              <div className="text-xs text-muted-foreground">Cancel anytime</div>
            </div>
            <div className="space-y-1">
              <div className="text-sm font-semibold text-accent">✓ Full Support</div>
              <div className="text-xs text-muted-foreground">Setup assistance included</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;